import type { TiptapDoc } from '@odo/shared-ui';
import apiV2 from '../api/v2';

/**
 * The timeline event that prompted an appointment (e.g. an
 * in-office-hold-note), snapshotted onto the appointment at creation time.
 * The backend only exposes practice-visible events here. `payload`'s shape
 * depends on `type` - see `components/appointments/originEventRenderers/`
 * for the per-type renderers that narrow it before display.
 */
export interface AppointmentOriginEvent {
    id: string;
    type: string;
    payload: Record<string, unknown>;
}

/**
 * Practice-wide appointment, as returned by GET /appointments/mine. Mirrors
 * `MineAppointmentListItem` in backend/src/routes/api/v2/appointments/mine/mine.service.ts.
 */
export interface Appointment {
    id: string;
    patientId: string;
    patientName: string;
    profilePictureUrl: string | null;
    date: string;
    time: string | null;
    reason: string | null;
    noteId: string | null;
    hasNote: boolean;
    canceledAt: string | null;
    /**
     * Snapshot of the timeline event that prompted this appointment,
     * captured server-side at creation time. Optional/nullable: older
     * appointments, or ones not prompted by an ODO hold, won't have one -
     * the Composer's ODO context card (edit mode) treats an absent snapshot
     * as "nothing to show" rather than an error.
     */
    originEvent?: AppointmentOriginEvent | null;
}

export interface AppointmentNoteInput {
    title: string;
    bodyDoc: TiptapDoc;
}

export interface CreateAppointmentInput {
    patientId: string;
    date: string;
    time?: string;
    reason?: string;
    // `null` is accepted by the backend as equivalent to omitting it (a new
    // appointment has no existing note to clear), kept for symmetry with
    // UpdateAppointmentInput.note so callers can share the same tri-state value.
    note?: AppointmentNoteInput | null;
}

export interface UpdateAppointmentInput {
    patientId: string;
    appointmentId: string;
    date?: string;
    time?: string | null;
    reason?: string | null;
    // Tri-state: omitted = leave the linked note as-is, `null` = explicitly
    // clear/unlink it (the backend soft-deletes it), an object = create-or-update it.
    note?: AppointmentNoteInput | null;
}

export interface CancelAppointmentInput {
    patientId: string;
    appointmentId: string;
    reason?: string;
}

export interface AppointmentMutationResult {
    id: string;
    patientId: string;
    practiceId: string;
    date: string;
    time: string | null;
    reason: string | null;
    noteId: string | null;
    canceledAt: string | null;
    exitedHold: boolean;
    mondaySynced: boolean;
}

/**
 * Practice-scoped, non-paginated appointment list, same "small enough to
 * filter client-side" pattern as `/patients/mine`. `includeCanceled` is
 * always requested so the modal can still show a canceled appointment that
 * was just acted on before its query invalidation lands; the UI itself
 * always excludes canceled rows from the agenda and calendar counts.
 */
export async function fetchMyAppointments(): Promise<Appointment[]> {
    const response = await apiV2.get<Appointment[]>('/appointments/mine', {
        params: { includeCanceled: true }
    });
    return response.data ?? [];
}

export async function createAppointment(
    input: CreateAppointmentInput
): Promise<AppointmentMutationResult> {
    const response = await apiV2.post<AppointmentMutationResult>(
        `/patients/${encodeURIComponent(input.patientId)}/appointment`,
        {
            date: input.date,
            ...(input.time !== undefined && { time: input.time }),
            ...(input.reason !== undefined && { reason: input.reason }),
            ...(input.note !== undefined && { note: input.note })
        }
    );
    return response.data;
}

export async function updateAppointment(
    input: UpdateAppointmentInput
): Promise<AppointmentMutationResult> {
    const response = await apiV2.patch<AppointmentMutationResult>(
        `/patients/${encodeURIComponent(input.patientId)}/appointment/${encodeURIComponent(input.appointmentId)}`,
        {
            ...(input.date !== undefined && { date: input.date }),
            ...(input.time !== undefined && { time: input.time }),
            ...(input.reason !== undefined && { reason: input.reason }),
            ...(input.note !== undefined && { note: input.note })
        }
    );
    return response.data;
}

export async function cancelAppointment(
    input: CancelAppointmentInput
): Promise<AppointmentMutationResult> {
    const response = await apiV2.post<AppointmentMutationResult>(
        `/patients/${encodeURIComponent(input.patientId)}/appointment/${encodeURIComponent(input.appointmentId)}/cancel`,
        { ...(input.reason !== undefined && { reason: input.reason }) }
    );
    return response.data;
}
